/* eslint-disable react-hooks/exhaustive-deps */
import { yupResolver } from '@hookform/resolvers/yup';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useCallback, useEffect, useState } from 'react';
import { Control, useForm, useWatch } from 'react-hook-form';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Button, Input, Upload } from '../../../../../../components';
import { Person } from '../../../../../../entities';
import { NewPerson, useFetch, usePersons } from '../../../../../../hooks';
import { format, parse } from '../../../../../../utils/date';
import { formatCpf, formatPhone } from '../../../../../../utils/helpers';
import { removeUndefinedValues } from '../../../../../../utils/objects';
import { PersonForm, schema } from '../../../../../../validations/person.validation';
import useStyles from './styles';

interface FormProps {
  person: Person;
  fileToUpload?: File | null;
  onSubmit: (input: Person | NewPerson) => unknown;
  handleUpload: (file: File | null) => void;
}

const PasswordIcon = ({
  className,
  isVisible,
  onClick,
}: {
  isVisible: boolean;
  className: string;
  onClick: () => void;
}): JSX.Element =>
  isVisible ? (
    <VisibilityOff className={className} onClick={onClick} />
  ) : (
    <Visibility className={className} onClick={onClick} />
  );

export const Form = ({ person, fileToUpload, onSubmit, handleUpload }: FormProps): JSX.Element => {
  const styles = useStyles();
  const {
    register,
    getValues,
    reset,
    resetField,
    control,
    handleSubmit: handleValidation,
    formState: { errors, isSubmitting },
  } = useForm<PersonForm>({ resolver: yupResolver(schema), mode: 'onChange' });
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const repository = usePersons();
  const [emailStatus, setEmailStatus] = useState<boolean>(false);
  const [isValidatingEmail, setIsValidatingEmail] = useState(true);

  const onClickSubmit = async () => {
    const values = getValues();

    return onSubmit({
      ...person,
      ...removeUndefinedValues(values),
      password: values.password,
      dateOfBirth:
        values.dateOfBirth && values.dateOfBirth.length > 0
          ? parse(values.dateOfBirth as unknown as string, 'dd/MM/yyyy', new Date())
          : undefined,
      socialName: values.socialName,
    });
  };

  useEffect(() => {
    const validEmail = async () => {
      const { valid } = await repository.checkEmailValidation(person.email);
      setEmailStatus(valid);
      setIsValidatingEmail(false);
    };

    if (person.email) validEmail();
  }, []);

  useEffect(() => {
    reset({
      email: person.email ?? '',
      name: person.name ?? '',
      identityNumber: person.identityNumber ? formatCpf(person.identityNumber) : '',
      phoneNumber: person.phoneNumber ? formatPhone(person.phoneNumber) : '',
      dateOfBirth: person.dateOfBirth ? format(person.dateOfBirth, 'dd/MM/yyyy') : '',
      socialName: person.socialName ?? '',
    });
  }, [person, reset]);

  const getEmailStatusText = () => {
    if (isValidatingEmail) return 'Verificando...';
    if (emailStatus === null) return 'Não verificado';
    return emailStatus ? 'Validado' : 'Não Validado';
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.left}>
          <Upload onUpload={handleUpload} file={fileToUpload ?? person.photoUrl} />
        </div>
        <div className={styles.right}>
          <div className={styles.line}>
            <Input
              label="nome completo"
              validationRegister={register('name')}
              placeholder="Ex: João da Silva Santos"
              fullWidth
              helperText={errors.name?.message || ' '}
              error={Boolean(errors.name)}
            />

            <Input
              label="nome social"
              validationRegister={register('socialName')}
              placeholder="Ex: Jota"
              fullWidth
              helperText={errors.socialName?.message || ' '}
              error={Boolean(errors.socialName)}
            />
          </div>

          <div className={styles.line}>
            <Input
              label="email"
              placeholder="Ex: joao@gmail.com"
              fullWidth
              validationRegister={register('email')}
              helperText={errors.email?.message || ' '}
              error={Boolean(errors.email)}
            />
            {person.id && (
              <Input
                label="validação do e-mail"
                sx={{ width: '25%' }}
                value={getEmailStatusText()}
                helperText={errors.email?.message || ' '}
                error={emailStatus && !emailStatus}
                disabled
              />
            )}
          </div>

          <div className={styles.line}>
            <Input
              label="cpf"
              placeholder="Ex: 000.000.000-00"
              fullWidth
              mask={{ mask: [{ mask: '000.000.000-00' }] }}
              validationRegister={register('identityNumber')}
              helperText={errors.identityNumber?.message || ' '}
              error={Boolean(errors.identityNumber)}
              onClick={() => {
                resetField('identityNumber', { defaultValue: '' });
              }}
            />
            <Input
              label="data de nascimento"
              placeholder="Ex: 18/02/1996"
              fullWidth
              mask={{ mask: [{ mask: '00/00/0000' }] }}
              validationRegister={register('dateOfBirth')}
              helperText={errors.dateOfBirth?.message || ' '}
              error={Boolean(errors.dateOfBirth)}
            />
            <Input
              label="telefone"
              autoComplete="off"
              placeholder="Ex: (00) 00000-0000"
              fullWidth
              mask={{ mask: [{ mask: '(00) 0000-0000' }, { mask: '(00) 00000-0000' }] }}
              control={control as unknown as Control}
              validationRegister={register('phoneNumber')}
              error={Boolean(errors.phoneNumber)}
              helperText={errors.phoneNumber?.message || ' '}
            />
          </div>

          {!person.id && (
            <div className={styles.line}>
              <Input
                label="senha"
                placeholder="digite a senha"
                fullWidth
                validationRegister={register('password')}
                type={isPasswordVisible ? 'text' : 'password'}
                helperText={errors.password?.message || ' '}
                error={Boolean(errors.password)}
                endIcon={
                  <PasswordIcon
                    className={styles.passwordIcon}
                    isVisible={isPasswordVisible}
                    onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                  />
                }
              />
            </div>
          )}
        </div>
      </div>
      <div className={styles.footer}>
        <Button sx={{ width: '200px' }} onClick={handleValidation(() => onClickSubmit())} loading={isSubmitting}>
          Salvar
        </Button>
      </div>
    </div>
  );
};

export default Form;
